<template>
  <v-container class="mto-sector-detail mx-2" fluid>
    <v-breadcrumbs class="px-0 pt-0 pb-2" :items="breadcrumbs" />

    <div class="mto-sector-detail__header">
      <div class="mto-sector-detail__header-left">
        <v-icon color="primary">mdi-map-marker-radius</v-icon>
        <h1 class="mto-sector-detail__title">{{ sector.name || 'Участок МТО' }}</h1>
      </div>
      <v-btn
        color="primary"
        outlined
        rounded
        class="text-none"
        :to="{ path: '/areas', query: { tab: 'mto-sectors' } }"
      >
        <v-icon left small>mdi-arrow-left</v-icon>
        К списку
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="!loading && sector.id">
      <v-row class="mb-4">
        <v-col cols="12" md="4">
          <v-card flat outlined class="mto-sector-detail__info pa-4">
            <h3 class="mto-sector-detail__section-title">Информация</h3>
            <div class="mto-sector-detail__meta mb-2">
              <span class="grey--text">Район обслуживания:</span> {{ sector.service_area_name || '—' }}
            </div>
            <div class="mto-sector-detail__meta">
              <span class="grey--text">Мастер:</span>
              <span v-if="sector.master && sector.master.name">{{ sector.master.name }}</span>
              <span v-else-if="sector.masters && sector.masters.length">
                {{ sector.masters.map(m => m.name).join(', ') }}
              </span>
              <span v-else>—</span>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="8">
          <v-card flat outlined class="pa-4">
            <h3 class="mto-sector-detail__section-title mb-3">Устройства на участке</h3>
            <v-row dense>
              <v-col
                v-for="type in deviceCountTypes"
                :key="type.key"
                cols="6"
                sm="4"
                md="3"
              >
                <div class="mto-sector-stat">
                  <div class="mto-sector-stat__value">{{ deviceCount(type.key) }}</div>
                  <div class="mto-sector-stat__label">{{ type.label }}</div>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <mto-sector-charts-card
        class="mb-4"
        :doorphones-indicators="sector.doorphones_indicators || {}"
        :equipment-counts="sector.equipment_counts || {}"
        :equipment-items="equipmentItems"
        :status-description="statusDescription"
      />

      <v-card flat outlined class="mto-sector-detail__card">
        <v-row align="center" class="pa-4 mb-0 areas-toolbar">
          <div class="areas-toolbar__search ml-3">
            <BaseTextField
              v-model="search"
              type-input="string"
              :is-button="false"
              label="Адрес"
            />
          </div>
          <v-spacer />
          <div class="areas-toolbar__actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="#4C6C93"
                  class="border card-footer"
                  v-bind="attrs"
                  v-on="on"
                  @click="loadSector"
                >
                  <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
                </v-btn>
              </template>
              <span>Обновить</span>
            </v-tooltip>

            <v-tooltip v-if="$checkPermission('import_doorphones_mto_sector')" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="#4C6C93"
                  class="border card-footer"
                  v-bind="attrs"
                  v-on="on"
                  @click="importDialog = true"
                >
                  <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-file-upload-outline</v-icon>
                </v-btn>
              </template>
              <span>Импорт CSV</span>
            </v-tooltip>

            <v-btn
              v-if="$checkPermission('attach_equipment_mto_sector')"
              color="primary"
              outlined
              rounded
              class="text-none mr-4"
              @click="attachDialog = true"
            >
              привязать устройство
            </v-btn>
          </div>
        </v-row>

        <v-tabs
          v-model="activeEquipmentTab"
          class="mto-sector-detail__equipment-tabs px-4"
          show-arrows="mobile"
        >
          <v-tab class="text-none">Основные</v-tab>
          <v-tab class="text-none">Калитки</v-tab>
          <v-tab class="text-none">Доп. входы</v-tab>
          <v-tab class="text-none">Шлагбаумы</v-tab>
        </v-tabs>

        <v-data-table
          class="table px-2 pb-2"
          :headers="equipmentHeaders"
          :items="filteredEquipmentItems"
          :footer-props="footerProps"
          :items-per-page="20"
          item-key="rowKey"
        >
          <template v-slot:item.id="{ item }">
            <router-link
              v-if="item.type === 'doorphone'"
              :to="`/doorphones/${item.id}`"
              class="routerLink"
            >
              {{ item.id }}
            </router-link>
            <router-link
              v-else-if="item.type === 'barrier'"
              :to="`/barrier/${item.id}`"
              class="routerLink"
            >
              {{ item.id }}
            </router-link>
            <router-link
              v-else-if="item.type === 'gate'"
              :to="`/gate/${item.id}/`"
              class="routerLink"
            >
              {{ item.id }}
            </router-link>
            <span v-else>{{ item.id }}</span>
          </template>
          <template v-slot:item.name="{ item }">
            <router-link
              v-if="item.type === 'doorphone'"
              :to="`/doorphones/${item.id}`"
              class="routerLink"
            >
              {{ item.name }}
            </router-link>
            <router-link
              v-else-if="item.type === 'barrier'"
              :to="`/barrier/${item.id}`"
              class="routerLink"
            >
              {{ item.name }}
            </router-link>
            <router-link
              v-else-if="item.type === 'gate'"
              :to="`/gate/${item.id}/`"
              class="routerLink"
            >
              {{ item.name }}
            </router-link>
            <span v-else>{{ item.name }}</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip v-if="$checkPermission('detach_equipment_mto_sector')" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  text
                  tile
                  small
                  v-bind="attrs"
                  v-on="on"
                  class="btn__icon pa-0"
                  width="18"
                  height="18"
                  @click.stop="$set(detachDialog, item.rowKey, true)"
                >
                  <v-icon size="large">mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Отвязать</span>
            </v-tooltip>
            <v-dialog v-model="detachDialog[item.rowKey]" width="500" content-class="dialog-border-radius">
              <BaseDeleteModal
                :active.sync="detachDialog[item.rowKey]"
                @close="$set(detachDialog, item.rowKey, false)"
                @submit="detachItem(item)"
                message-title="Отвязать устройство?"
                :message-text="`Подтверждая, вы отвяжете «${item.name}» от участка МТО.`"
                confirm-text="Отвязать"
                cancel-text="Отмена"
                confirm-color="error"
              />
            </v-dialog>
          </template>
        </v-data-table>
      </v-card>
    </template>

    <v-dialog v-model="importDialog" max-width="480" content-class="dialog-border-radius">
      <v-card>
        <v-card-title>Импорт домофонов из CSV</v-card-title>
        <v-card-text>
          <p class="body-2 grey--text text--darken-1 mb-3">
            CSV-файл должен содержать только ID домофонов — по одному на строку.
            Домофоны другого агента не будут привязаны.
          </p>
          <v-file-input
            v-model="importFile"
            label="CSV-файл"
            accept=".csv,.txt"
            outlined
            dense
            show-size
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text class="text-none" @click="importDialog = false">Отмена</v-btn>
          <v-btn
            color="primary"
            class="text-none"
            :loading="importing"
            :disabled="!importFile"
            @click="doImport"
          >
            Импортировать
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <mto-sector-attach-dialog
      v-if="sector.id"
      v-model="attachDialog"
      :sector-id="sector.id"
      :agent-id="sector.agent_id"
      :attached-ids="attachedEquipmentIds"
      @attached="loadSector"
    />
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BaseTextField from '@/components/BaseTextField';
import BaseDeleteModal from '@/components/BaseDeleteModal';
import MtoSectorAttachDialog from '@/components/areas/MtoSectorAttachDialog';
import MtoSectorChartsCard from '@/components/areas/MtoSectorChartsCard';

export default {
  name: 'MtoSectorDetail',
  components: { BaseTextField, BaseDeleteModal, MtoSectorAttachDialog, MtoSectorChartsCard },
  data () {
    return {
      loading: false,
      search: '',
      searchTimer: null,
      importDialog: false,
      importFile: null,
      importing: false,
      attachDialog: false,
      detachDialog: {},
      activeEquipmentTab: 0,
      statusDescription: {},
      footerProps: {
        showFirstLastPage: true,
        'items-per-page-options': [10, 20, 50, 100],
        'items-per-page-text': 'Строк на странице',
      },
      deviceCountTypes: [
        { key: 'doorphones', label: 'Домофоны' },
        { key: 'additional_entrances', label: 'Доп. входы' },
        { key: 'gates_doorphones', label: 'Калитки (домофон)' },
        { key: 'gates_readers', label: 'Калитки (считыватель)' },
        { key: 'apartments', label: 'Всего квартир' },
        { key: 'apartments_with_phone_setup', label: 'Квартир с возможностью установки трубки' },
        { key: 'barriers', label: 'Шлагбаумы' },
      ],
      equipmentHeaders: [
        { text: 'ID', value: 'id', width: '10%' },
        { text: 'Название', value: 'name', sortable: false },
        { text: 'Адрес', value: 'address', sortable: false },
        { text: '', value: 'actions', width: '8%', sortable: false, align: 'center' },
      ],
    };
  },
  created () {
    this.loadSector();
  },
  watch: {
    '$route.params.id' () {
      this.loadSector();
    },
    search () {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.loadSector();
      }, 400);
    },
  },
  computed: {
    ...mapGetters({
      sector: 'MtoSector/mtoSector',
    }),
    breadcrumbs () {
      return [
        { text: 'Районы', disabled: false, href: '/areas?tab=mto-sectors' },
        { text: this.sector.name || 'Участок МТО', disabled: true },
      ];
    },
    equipmentItems () {
      return (this.sector.equipment || []).map(item => ({
        ...item,
        rowKey: `${item.type}-${item.id}`,
      }));
    },
    filteredEquipmentItems () {
      if (this.activeEquipmentTab === 1) {
        return this.equipmentItems.filter(item => {
          if (item.type === 'gate') return true;
          return item.type === 'doorphone' && item.doorphone_type === 'G';
        });
      }
      if (this.activeEquipmentTab === 2) {
        return this.equipmentItems.filter(item => (
          item.type === 'doorphone' && item.doorphone_type === 'A'
        ));
      }
      if (this.activeEquipmentTab === 3) {
        return this.equipmentItems.filter(item => item.type === 'barrier');
      }
      return this.equipmentItems.filter(item => {
        if (item.type === 'controller') return true;
        if (item.type !== 'doorphone') return false;
        return item.doorphone_type !== 'G' && item.doorphone_type !== 'A';
      });
    },
    attachedEquipmentIds () {
      const equipment = this.sector.equipment || [];
      return {
        doorphone: equipment.filter(item => item.type === 'doorphone').map(item => item.id),
        barrier: equipment.filter(item => item.type === 'barrier').map(item => item.id),
      };
    },
  },
  methods: {
    ...mapActions({
      getMtoSector: 'MtoSector/getMtoSector',
      importDoorphones: 'MtoSector/importDoorphones',
      detachEquipment: 'MtoSector/detachEquipment',
    }),
    deviceCount (key) {
      if (!this.sector.equipment_counts) return 0;
      return this.sector.equipment_counts[key] || 0;
    },
    async loadSector () {
      this.loading = true;
      try {
        const result = await this.getMtoSector({
          id: this.$route.params.id,
          options: { search: this.search || undefined },
        });
        this.statusDescription = result.status_description || {};
      } catch (e) {
        this.$flash_warning('Не удалось загрузить участок МТО');
      } finally {
        this.loading = false;
      }
    },
    async doImport () {
      if (!this.importFile) return;
      this.importing = true;
      try {
        const result = await this.importDoorphones({
          id: this.$route.params.id,
          file: this.importFile,
        });
        const attached = result.data?.attached ?? 0;
        const errors = result.data?.errors || [];
        errors.forEach(err => this.$flash_warning(err));
        this.$flash_success(`Привязано домофонов: ${attached}`);
        this.importDialog = false;
        this.importFile = null;
        await this.loadSector();
      } catch (e) {
        this.$flash_warning('Ошибка при импорте');
      } finally {
        this.importing = false;
      }
    },
    async detachItem (item) {
      try {
        await this.detachEquipment({
          id: this.$route.params.id,
          data: { type: item.type, id: item.id },
        });
        this.$set(this.detachDialog, item.rowKey, false);
        this.$flash_success('Устройство отвязано');
        await this.loadSector();
      } catch (e) {
        this.handleError(e, 'Не удалось отвязать устройство');
      }
    },
    handleError (err, fallback) {
      if (err.response && err.response.status === 422) {
        const response = err.response.data;
        Object.keys(response).forEach((input) => {
          if (Array.isArray(response[input])) {
            response[input].forEach(error => this.$flash_warning(error));
          }
        });
      } else if (err.response && err.response.data && err.response.data.error) {
        this.$flash_warning(err.response.data.error);
      } else {
        this.$flash_warning(fallback);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../components/areas/areasList.scss';

.mto-sector-detail__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.mto-sector-detail__header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mto-sector-detail__title {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
}

.mto-sector-detail__section-title {
  font-size: 1.0625rem;
  font-weight: 600;
  margin: 0 0 12px;
}

.mto-sector-detail__meta {
  font-size: 1rem;
  line-height: 1.5;
}

.mto-sector-detail__card {
  border-radius: 12px;
  overflow: hidden;
}

.mto-sector-stat {
  text-align: center;
  padding: 12px 8px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  min-height: 72px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &__value {
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.2;
  }

  &__label {
    font-size: 0.875rem;
    color: #78909c;
    margin-top: 4px;
  }
}

::v-deep a.routerLink {
  color: inherit;
  text-decoration: none;
}

::v-deep a.routerLink:hover {
  text-decoration: underline;
}

.mto-sector-detail__equipment-tabs ::v-deep .v-tabs-bar {
  background: #f5f7f8;
}

.mto-sector-detail__equipment-tabs ::v-deep .v-tab {
  text-transform: none;
  font-weight: 500;
}
</style>
